import type { IntakeSpec, FurnitureBedDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';

function getDetails(intake: IntakeSpec): FurnitureBedDetails {
  return intake.details as FurnitureBedDetails;
}

type MattressDims = { length: number; width: number };

function getMattressDims(bedSize: FurnitureBedDetails['bedSize']): MattressDims {
  switch (bedSize) {
    case 'single':      return { length: 1950, width: 970 };
    case 'semi_double': return { length: 1950, width: 1200 };
    case 'double':      return { length: 1950, width: 1400 };
    case 'queen':       return { length: 2010, width: 1600 };
    case 'king':        return { length: 2010, width: 1800 };
  }
}

const FRAME_HEIGHT = 400;   // foot-side post height (also head-side when no headboard)
const HEAD_POST_HEIGHT = 1100;
const POST_SECTION = 80;
const RAIL_WIDTH = 200;
const RAIL_THICKNESS = 25;
const SLAT_WIDTH = 80;
const SLAT_THICKNESS = 15;

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const mattress = getMattressDims(d.bedSize);
  const mL = mattress.length;
  const mW = mattress.width;
  const headPostH = d.hasHeadboard ? HEAD_POST_HEIGHT : FRAME_HEIGHT;

  const out: Part[] = [];

  // 4 corner posts
  // Foot side (posts 1 & 2), head side (posts 3 & 4)
  const postPositions: Array<{ id: string; name: string; length: number; pos: [number, number, number] }> = [
    { id: 'post_foot_left',  name: '脚柱（足元左）', length: FRAME_HEIGHT,  pos: [-(mW / 2), -(mL / 2), 0] },
    { id: 'post_foot_right', name: '脚柱（足元右）', length: FRAME_HEIGHT,  pos: [mW / 2,  -(mL / 2), 0] },
    { id: 'post_head_left',  name: '脚柱（頭元左）', length: headPostH,     pos: [-(mW / 2), mL / 2,  0] },
    { id: 'post_head_right', name: '脚柱（頭元右）', length: headPostH,     pos: [mW / 2,  mL / 2,  0] },
  ];

  for (const p of postPositions) {
    out.push({
      id: p.id,
      name: p.name,
      kind: 'leg',
      materialId: 'mat-primary',
      length: p.length,
      crossSection: { shape: 'square', side: POST_SECTION },
      position: p.pos,
      rotation: [0, 0, 0],
      tags: ['post'],
      paramRefs: [],
    });
  }

  // 2 long side rails (left/right)
  out.push(
    {
      id: 'rail_left',
      name: 'サイドレール（左）',
      kind: 'rail',
      materialId: 'mat-primary',
      length: mL,
      width: RAIL_WIDTH,
      thickness: RAIL_THICKNESS,
      position: [-(mW / 2), 0, FRAME_HEIGHT - RAIL_WIDTH / 2],
      rotation: [0, 0, 0],
      tags: ['rail', 'side'],
      paramRefs: [],
    },
    {
      id: 'rail_right',
      name: 'サイドレール（右）',
      kind: 'rail',
      materialId: 'mat-primary',
      length: mL,
      width: RAIL_WIDTH,
      thickness: RAIL_THICKNESS,
      position: [mW / 2, 0, FRAME_HEIGHT - RAIL_WIDTH / 2],
      rotation: [0, 0, 0],
      tags: ['rail', 'side'],
      paramRefs: [],
    },
  );

  // 2 end rails (head/foot)
  out.push(
    {
      id: 'rail_foot',
      name: 'エンドレール（足元）',
      kind: 'rail',
      materialId: 'mat-primary',
      length: mW,
      width: RAIL_WIDTH,
      thickness: RAIL_THICKNESS,
      position: [0, -(mL / 2), FRAME_HEIGHT - RAIL_WIDTH / 2],
      rotation: [0, 0, 90],
      tags: ['rail', 'end'],
      paramRefs: [],
    },
    {
      id: 'rail_head',
      name: 'エンドレール（頭元）',
      kind: 'rail',
      materialId: 'mat-primary',
      length: mW,
      width: RAIL_WIDTH,
      thickness: RAIL_THICKNESS,
      position: [0, mL / 2, FRAME_HEIGHT - RAIL_WIDTH / 2],
      rotation: [0, 0, 90],
      tags: ['rail', 'end'],
      paramRefs: [],
    },
  );

  // Headboard
  if (d.hasHeadboard) {
    out.push({
      id: 'headboard',
      name: 'ヘッドボード',
      kind: 'panel',
      materialId: 'mat-primary',
      width: mW,
      depth: 15,
      thickness: 900,
      position: [0, mL / 2 + 15 / 2, FRAME_HEIGHT + 900 / 2],
      rotation: [0, 0, 0],
      tags: ['headboard'],
      paramRefs: [],
      cutouts: [],
    });
  }

  // Footboard
  if (d.hasFootboard) {
    out.push({
      id: 'footboard',
      name: 'フットボード',
      kind: 'panel',
      materialId: 'mat-primary',
      width: mW,
      depth: 15,
      thickness: 400,
      position: [0, -(mL / 2 + 15 / 2), FRAME_HEIGHT + 400 / 2],
      rotation: [0, 0, 0],
      tags: ['footboard'],
      paramRefs: [],
      cutouts: [],
    });
  }

  // Slats
  const slatCount = d.slatCount;
  const slatSpacing = mL / (slatCount + 1);
  for (let i = 0; i < slatCount; i++) {
    const yPos = -(mL / 2) + slatSpacing * (i + 1);
    out.push({
      id: `slat_${i + 1}`,
      name: `スラット ${i + 1}`,
      kind: 'rail',
      materialId: 'mat-primary',
      length: mW - 50,
      width: SLAT_WIDTH,
      thickness: SLAT_THICKNESS,
      position: [0, yPos, FRAME_HEIGHT],
      rotation: [0, 0, 90],
      tags: ['slat'],
      paramRefs: [],
    });
  }

  // Under-bed storage drawer fronts
  if (d.hasUnderStorage) {
    out.push(
      {
        id: 'drawer_front_left',
        name: '引出し前板（左）',
        kind: 'panel',
        materialId: 'mat-primary',
        width: mW / 2 - POST_SECTION,
        depth: 15,
        thickness: FRAME_HEIGHT - 50,
        position: [-(mW / 4), -(mL / 2), (FRAME_HEIGHT - 50) / 2 + 25],
        rotation: [0, 0, 0],
        tags: ['drawer_front'],
        paramRefs: [],
        cutouts: [],
      },
      {
        id: 'drawer_front_right',
        name: '引出し前板（右）',
        kind: 'panel',
        materialId: 'mat-primary',
        width: mW / 2 - POST_SECTION,
        depth: 15,
        thickness: FRAME_HEIGHT - 50,
        position: [mW / 4, -(mL / 2), (FRAME_HEIGHT - 50) / 2 + 25],
        rotation: [0, 0, 0],
        tags: ['drawer_front'],
        paramRefs: [],
        cutouts: [],
      },
    );
  }

  return out;
}

export function joints(intake: IntakeSpec): Joint[] {
  const d = getDetails(intake);
  const joinery = d.joineryType;

  const mattress = getMattressDims(d.bedSize);
  const mL = mattress.length;
  const mW = mattress.width;

  // mortise_tenon: emit real MortiseTenonJoint for the 8 rail→post connections.
  if (joinery === 'mortise_tenon') {
    const out: Joint[] = [];
    const tenonLength = 25;
    const halfPost = POST_SECTION / 2;
    const meetZ = FRAME_HEIGHT - RAIL_WIDTH / 2;

    type Pair = { id: string; railId: string; postId: string; railLength: number; railEnd: 'a' | 'b'; axis: 'x' | 'y' };
    const pairs: Pair[] = [
      { id: 'mt_rail_left_foot',  railId: 'rail_left',  postId: 'post_foot_left',  railLength: mL, railEnd: 'a', axis: 'y' },
      { id: 'mt_rail_left_head',  railId: 'rail_left',  postId: 'post_head_left',  railLength: mL, railEnd: 'b', axis: 'y' },
      { id: 'mt_rail_right_foot', railId: 'rail_right', postId: 'post_foot_right', railLength: mL, railEnd: 'a', axis: 'y' },
      { id: 'mt_rail_right_head', railId: 'rail_right', postId: 'post_head_right', railLength: mL, railEnd: 'b', axis: 'y' },
      { id: 'mt_rail_foot_left',  railId: 'rail_foot',  postId: 'post_foot_left',  railLength: mW, railEnd: 'a', axis: 'x' },
      { id: 'mt_rail_foot_right', railId: 'rail_foot',  postId: 'post_foot_right', railLength: mW, railEnd: 'b', axis: 'x' },
      { id: 'mt_rail_head_left',  railId: 'rail_head',  postId: 'post_head_left',  railLength: mW, railEnd: 'a', axis: 'x' },
      { id: 'mt_rail_head_right', railId: 'rail_head',  postId: 'post_head_right', railLength: mW, railEnd: 'b', axis: 'x' },
    ];
    for (const p of pairs) {
      const railEndLocal: [number, number, number] =
        p.railEnd === 'a' ? [-p.railLength / 2, 0, 0] : [p.railLength / 2, 0, 0];
      out.push({
        id: p.id,
        fromPartId: p.railId,
        toPartId: p.postId,
        type: 'mortise_tenon',
        tenonWidth: 60,
        tenonHeight: 30,
        tenonLength,
        shoulder: 5,
        haunched: false,
        clearance: 0.2,
        axis: p.axis,
        fromPosition: railEndLocal,
        toPosition: [0, 0, meetZ + halfPost],
      });
    }
    if (d.hasHeadboard) {
      out.push(
        { id: 'headboard_to_post_left',  fromPartId: 'headboard', toPartId: 'post_head_left',  type: 'screw', size: 'M8', length: 60, count: 2 },
        { id: 'headboard_to_post_right', fromPartId: 'headboard', toPartId: 'post_head_right', type: 'screw', size: 'M8', length: 60, count: 2 },
      );
    }
    return out;
  }

  // bed_bolt: no dedicated joint kind in B2 core / B2 rest. Use M8 screws
  // (typical for bed bolts). cam_lock: also no dedicated kind, M10 like default.
  // 
  const boltSize: 'M8' | 'M10' = joinery === 'bed_bolt' ? 'M8' : 'M10';
  const boltLength = joinery === 'bed_bolt' ? 90 : 80;

  const out: Joint[] = [];

  // 8 rail→post joints
  const railPostPairs: Array<[string, string, string]> = [
    ['rail_left_foot',  'rail_left',  'post_foot_left'],
    ['rail_left_head',  'rail_left',  'post_head_left'],
    ['rail_right_foot', 'rail_right', 'post_foot_right'],
    ['rail_right_head', 'rail_right', 'post_head_right'],
    ['rail_foot_left',  'rail_foot',  'post_foot_left'],
    ['rail_foot_right', 'rail_foot',  'post_foot_right'],
    ['rail_head_left',  'rail_head',  'post_head_left'],
    ['rail_head_right', 'rail_head',  'post_head_right'],
  ];

  for (const [jId, fromId, toId] of railPostPairs) {
    out.push({
      id: jId,
      fromPartId: fromId,
      toPartId: toId,
      type: 'screw',
      size: boltSize,
      length: boltLength,
      count: 2,
    });
  }

  // Headboard joints (one each side)
  if (d.hasHeadboard) {
    out.push(
      { id: 'headboard_to_post_left',  fromPartId: 'headboard', toPartId: 'post_head_left',  type: 'screw', size: 'M8', length: 60, count: 2 },
      { id: 'headboard_to_post_right', fromPartId: 'headboard', toPartId: 'post_head_right', type: 'screw', size: 'M8', length: 60, count: 2 },
    );
  }

  return out;
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const out: Feature[] = [];

  // Edge chamfer 2mm on all rail edges
  const railIds = ['rail_left', 'rail_right', 'rail_foot', 'rail_head'];
  for (const rId of railIds) {
    out.push({
      id: `chamfer_${rId}`,
      partId: rId,
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 2,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  return out;
}
